<template>
  <div class="detailed-indicator-widget f-w f-h">
    <div v-if="windicator" class="f-w f-h">
      <DetailedIndicator
        ref="detailed"
        as-component
        emit-new-range
        :cumulated="cumulated == 1"
        :analysis="displayAnalysis == 1 && cumulated == 1"
        :hide-bottom-section="displayDetails == 0"
        :hide-pb-actions="displayPbActions == 0 || cumulated == 1"
        :bound-config="selectedConfig"
        :focus-period-indexes="focusPeriodIndexes"
        :team-ids="utilsSelectedTeamIds"
        @rangeEdit="rangeEdited"
      />
    </div>
    <div v-else class="f-w f-h h-v-centerer">
      <span v-if="!cfgTemplateEdition">{{ $t('message.WIDGET_INDIC_NO_SELECTED') }}</span>
    </div>
    <div v-if="loading" class="loading">
      <img src="/images/logo300.png" alt="">
    </div>
  </div>
</template>

<script>
/**
 * Integer number used as a boolean (false when equal to 0, true when > 0)
 * @typedef {Number} BooleanNumber
 */
/**
 * Widget holder for detailed-indicator.vue with predefined configuration
 * @vue-prop {BooleanNumber} cumulated - Whether the indicator picked is from cumulated managed indicators
 * @vue-prop {Number} indicatorId - ID of the indicator picked. If cumulated=1, ID of the template
 * @vue-prop {Number} [configId=0] - ID of 'IndicatorDisplayConfig'. If 0, no config selected
 * @vue-prop {Number} indicatorId - ID of the indicator picked. If cumulated=1, ID of the template
 * @vue-prop {BooleanNumber} displayDetails - Whether the bottom section should be displayed or not
 * @vue-prop {BooleanNumber} displayAnalysis - Only available when cumulated = 1. Display a pareto chart
 * @vue-prop {BooleanNumber} displayPbActions - Only available when cumulated = 0. Display the
 *           pbs / actions related to this indicator on the selected time range
 */

import { isEqual } from 'lodash';
// State management
import { mapState } from 'vuex';
import {
  FETCH_INDICATOR_THEMES,
  FETCH_MANAGED_INDICATORS,
  FETCH_LIBRARY_INDICATORS,
  BUFFER_FETCH_INDICATORS,
  BUFFER_FETCH_CUMULATED_INDICATORS,
  FETCH_INDICATOR_DISPLAY_CONFIGS,
} from '@/store/action-types';
import {
  GET_INDICATOR_THEMES,
  GET_MANAGED_INDICATORS,
  GET_LIBRARY_INDICATORS,
  GET_INDICATOR_DISPLAY_CONFIGS,
} from '@/store/mutation-types';
// Components
import DetailedIndicator from '@/views/overlay/detailed-indicator.vue';
// Parent
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import mixinManagedTeamsUtils from '@/mixins/managed-teams-utils.vue';
// Utils
import { mapLibraryToRadio } from '@/utils/indicator-utils';


export default {

  name: 'DetailedIndicatorWidget',

  components: {
    DetailedIndicator,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinManagedTeamsUtils],

  props: {
    cumulated: {
      type: Number,
      default: 0,
      validator: v => v === 0 || v === 1,
    },
    indicatorId: {
      type: Number,
      default: 0,
    },
    // WARNING: Used in cfg page assignation
    libraryIndicatorId: {
      type: Number,
      default: 0,
    },
    configId: {
      type: Number,
      default: 0,
    },
    displayDetails: {
      type: Number,
      default: 1,
      validator: v => v === 0 || v === 1,
    },
    displayAnalysis: {
      type: Number,
      default: 0,
      validator: v => v === 0 || v === 1,
    },
    displayPbActions: {
      type: Number,
      default: 1,
      validator: v => v === 0 || v === 1,
    },
    teamsFilter: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      focusPeriodIndexes: { start: 15, end: 1 },
    };
  },

  computed: {
    config() {
      const config = [
        {
          key: 'detailedIndicTopSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'cumulated',
          value: this.cumulated,
          component: 'IntCheckbox',
          options: {
            class: 'mt3em',
            title: this.$t('message.WIDGET_INDIC_CUULATED'),
          },
        },
        {
          key: 'configId',
          value: this.configId,
          component: 'IndicatorDisplayConfigField',
          options: {
            class: 'mt3em',
            detailedIndicatorHint: true,
          },
        },
        {
          key: 'displayDetails',
          value: this.displayDetails,
          component: 'IntCheckbox',
          options: {
            class: 'mt5em block',
            title: this.$t('message.WIDGET_DETAILED_DETAILS'),
          },
        },
      ];
      if (this.cumulated) {
        config.push({
          key: 'displayAnalysis',
          value: this.displayAnalysis,
          component: 'IntCheckbox',
          options: {
            class: 'mt3em block',
            title: this.$t('message.WIDGET_DETAILED_ANALYSIS'),
          },
        });
        if (!this.cfgTemplateEdition) {
          // ALlow filtering teams from which the values are fetched
          config.push({
            key: 'teamsFilter',
            value: this.utilsSelectedTeamIds,
            component: 'AdvancedTeamPicker',
            options: {
              legend: this.$t('message.TEAMS') + this.$t('message.TWO_DOTS'),
              class: 'mt3em',
            },
          });
        }
      } else if (this.displayDetails) {
        config.push({
          key: 'displayPbActions',
          value: this.displayPbActions,
          component: 'IntCheckbox',
          options: {
            class: 'mt5 block',
            title: this.$t('message.WIDGET_DETAILED_PB_ACTIONS'),
          },
        });
      }

      // When editing a page template, the user may only choose amongst library. See indicator-widget.vue
      if (!this.cfgTemplateEdition) {
        config.splice(2, 0, {
          key: 'detailedIndicIndicTopSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        });
        config.splice(3, 0, {
          key: 'indicatorId',
          value: this.indicatorId,
          component: 'EditableLabelRadio',
          options: {
            class: 'mt3em',
            legend: this.$t('message.INDICATOR'),
            items: this.cumulated ? this.allLibraryThemes : this.allIndicators,
            labelKey: 'name',
            placeholder: this.$t('message.NONE'),
          },
        });
        config.splice(4, 0, {
          key: 'detailedIndicIndicBotSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        });
      } else {
        config.splice(2, 0, {
          key: 'libraryIndicatorId',
          value: this.libraryIndicatorId,
          component: 'EditableLabelRadio',
          options: {
            class: 'mt3em',
            legend: this.$t('message.INDICATOR'),
            items: this.allLibraryThemes,
            labelKey: 'name',
            placeholder: this.$t('message.NONE'),
          },
        });
        config.splice(2, 0, {
          key: 'libraryIndicatorIdHint',
          value: '',
          component: 'Div',
          options: {
            class: 'field-hint',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.CFG_PAGE_LIBRARY_INDIC_HINT'),
          }],
        });
      }
      config.push(this.defaultBgColorConfig);
      return config;
    },
    ...mapState({
      // Loading
      themeIndicatorsLoading: state => state[GET_INDICATOR_THEMES.loadingKey],
      libraryIndicatorsLoading: state => state[GET_LIBRARY_INDICATORS.loadingKey],
      managedIndicatorsLoading: state => state[GET_MANAGED_INDICATORS.loadingKey],
      configsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
      // Data
      libraryIndicators: state => state[GET_LIBRARY_INDICATORS.stateKey] || {},
      managedIndicators: state => state[GET_MANAGED_INDICATORS.stateKey] || [],
      configs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
      stateIndicators: state => state.indicators,
      cumulatedIndicators: state => state.cumulatedIndicators,
    }),
    /** Array: Default and custom configs */
    allPresets() {
      return [...this.$store.getters.indicDisplayConfigs, ...this.configs];
    },
    /** Indicators to pick from */
    allIndicators() {
      const indicators = [{
        title: this.$t('message.WIDGET_INDICS_OF_CURRENT_TEAM'),
        radioItems: this.$store.getters.themeIndicators || [],
      }];
      this.managedIndicators.forEach((team) => {
        if (Number(team.id) !== this.currentTeam.id) {
          const teamIndicators = [];
          team.themes.forEach((theme) => {
            teamIndicators.push(...theme.indicators.map(indic => ({
              id: Number(indic.id),
              name: `${indic.name} - ${theme.name} (${team.name})`,
            })));
          });
          indicators.push({
            title: team.name,
            radioItems: teamIndicators,
          });
        }
      });
      return indicators;
    },
    /** Library indicators to pick from */
    allLibraryThemes() {
      return mapLibraryToRadio(this, this.libraryIndicators);
    },
    range() {
      const range = {};
      range.start = this.selectedConfig && this.selectedConfig.configStart
        ? this.$moment(this.selectedConfig.configStart)
        : this.$moment().subtract(365, 'd');
      range.end = this.$moment(range.start).add(365, 'd');
      return range;
    },
    /** Indicator object including values */
    windicator() {
      const id = this.indicatorId;
      return this.cumulated ? this.cumulatedIndicators[id] : this.stateIndicators[id];
    },
    /** Config object picked by the user */
    selectedConfig() {
      return this.allPresets.find(config => Number(config.id) === Number(this.configId));
    },
    /** NOTE: Used in mixins/managed-team-utils to get the value of utilsSelectedTeamIds */
    utilsSelectedTeamIdsToFilter() {
      return this.teamsFilter;
    },
  },

  watch: {
    indicatorId() {
      // Indicator changed = fetch new data
      this.refresh(true);
    },
    utilsSelectedTeamIds(newVal, oldVal) {
      if (this.cumulated && !isEqual(newVal, oldVal)) {
        this.refresh(true);
      }
    },
    selectedConfig(newVal, oldVal) {
      // New config = new chart
      if (!isEqual(newVal, oldVal)) {
        this.$nextTick(() => {
          this.loading = true;
          this.refresh(true);
        });
      }
    },
    displayAnalysis() {
      // Analysis param updated = charts will need an update and pareto chart must be drawn
      const comp = this.$refs.detailed;
      if (comp) {
        this.updateCharts();
      }
    },
    displayDetails() {
      // Details param updated => Charts resized
      this.updateCharts();
    },
    displayPbActions() {
      // pb-actions update => Charts resized
      this.updateCharts();
    },
    windicator(newVal) {
      this.loading = false;
      if (!newVal) {
        return;
      }
      this.$nextTick(() => {
        const comp = this.$refs.detailed;
        if (comp) {
          // Update data
          comp.indicator = newVal;
          // Range may have been edited
          this.updateFromQuery();
          this.updateCharts();
        }
      });
    },
    // Indicators are done loading, update the list if currently displayed
    themeIndicatorsLoading(isLoading) {
      if (!isLoading) {
        this.$emit('updateConfigOptions');
      }
    },
    managedIndicatorsLoading(isLoading) {
      if (!isLoading) {
        this.$emit('updateConfigOptions');
      }
    },
  },

  mounted() {
    if (this.editable && !this.libraryIndicatorsLoading) {
      this.$store.dispatch(FETCH_LIBRARY_INDICATORS);
    }
    // Edition of a template, indicators of teams are not needed
    // No need to refresh either as no indicator will be displayed here
    if (this.cfgTemplateEdition) {
      return;
    }
    if (this.editable && !this.themeIndicatorsLoading) {
      // Allow choosing between team's indicators and managed teams indicators
      this.$store.dispatch(FETCH_INDICATOR_THEMES);
    }
    if (this.editable && !this.configsLoading) {
      this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
    }
    if (!this.managedIndicatorsLoading && (this.editable || this.cumulated)) {
      this.$store.dispatch(FETCH_MANAGED_INDICATORS, { noValues: true, teams: true });
    }
    this.refresh(true);
  },

  methods: {
    /** Refresh after a config update or after changing the range */
    refresh(fetchData) {
      if (this.cfgTemplateEdition) {
        return;
      }
      if (fetchData) {
        if (this.indicatorId > 0) {
          // Don't call refresh on DetailedIndicator component since it doesn't support cumulated template
          let action = BUFFER_FETCH_INDICATORS;
          let payload = { values: true, indicatorId: [this.indicatorId] };
          if (this.cumulated) {
            action = BUFFER_FETCH_CUMULATED_INDICATORS;
            payload = {
              libraryIds: [this.indicatorId],
              teams: this.utilsSelectedTeamIds.length ? this.utilsSelectedTeamIds : true,
            };
          }
          payload.range = this.range;
          this.$store.dispatch(action, payload);
        }
      } else {
        this.updateFromQuery();
      }
    },
    /** Redraw charts and reset range selector position */
    updateCharts() {
      if (this.cfgTemplateEdition) {
        return;
      }
      this.$nextTick(() => {
        const comp = this.$refs.detailed;
        comp.makeCharts(true);
        comp.placeRange();
      });
    },
    /** To be called when query params are edited */
    updateFromQuery() {
      const comp = this.$refs.detailed;
      const { rangeStart, rangeEnd } = this.getQueryParams();
      if (comp && rangeStart !== undefined && rangeEnd !== undefined) {
        this.focusPeriodIndexes = { start: Number(rangeStart), end: Number(rangeEnd) };
      }
    },
    /** Range to display detailed bottom section was edited */
    rangeEdited(range) {
      this.setQueryParams({ rangeStart: range.start, rangeEnd: range.end });
    },
    /** Working days may have changed. Update */
    userLoaded() {
      this.refresh();
    },
    sizeChanged() {
      this.updateCharts();
    },
  },
};

</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.detailed-indicator-widget.widget-component-child {
  overflow: hidden;
  position: relative;

  .basic-gray-section.white.detailed-indicator {
    background: initial;
    .content {
      background: initial;
    }
  }

  .detailed-indicator .content .top-section .canvas-wrapper {
    padding: 0px;
  }
  .loading {
    width: 60px;
    height: 60px;
    border-radius: 50px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0px 0px 25px 15px #ffffff;
    background: #ffffff;

    img {
      position: absolute;
      top: 7px;
      left: 7px;
      width: 46px;
      height: 46px;
      animation: rotation 2s infinite linear;
      -webkit-animation: rotation 2s infinite linear;
    }
  }
}
</style>
